import { useAuth0 } from '@auth0/auth0-react';
import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { UserContextType, UserInfo } from '../types/user';
import { useNavigate } from 'react-router-dom';
import { addAccessTokenInterceptor } from '@/services/api';
import { useQuery } from '@tanstack/react-query';
import { getUserCurrent } from '@/services/user-service';

export const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const { user, isAuthenticated, getAccessTokenSilently, logout: auth0Logout } = useAuth0();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

  const { data: userCurrent } = useQuery({
    queryKey: ['userCurrent'],
    queryFn: getUserCurrent,
    enabled: !!isAuthenticated,
  });

  useEffect(() => {
    const getUserInfo = async () => {
      if (isAuthenticated && user) {
        try {
          setUserInfo({
            email: user.email ?? '',
            role: 'Admin',
            userId: userCurrent?.userId ?? null,
            userName: userCurrent?.userName ?? '',
            organisation: userCurrent?.organisation ?? '',
            systemPermissions: userCurrent?.systemPermissions ?? [],
          });
        } catch (error) {
          console.error('Error getting user info', error);
        }
      }
    };

    getUserInfo();
  }, [isAuthenticated, user, userCurrent]);

  useEffect(() => {
    addAccessTokenInterceptor(getAccessTokenSilently)
  }, [getAccessTokenSilently])

  const value = useMemo(() => ({
    userInfo,
    logout: async () => {
      setUserInfo(null);
      await auth0Logout({ logoutParams: { returnTo: 'https://localhost:3000/login' } });
      navigate('/login')
    }
  }), [userInfo, auth0Logout]);

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};